import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Vault, RefreshCw, LogOut, FileText, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem("em_admin_key") || "");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("em_admin_key"));
  const [applications, setApplications] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/applications`, {
        headers: { "X-Admin-Key": k },
      });
      setApplications(res.data.applications || []);
      setStatuses(res.data.statuses || []);
      try {
        const b = await axios.get(`${API}/bookings`, { headers: { "X-Admin-Key": k } });
        setBookings(b.data.bookings || []);
      } catch {
        setBookings([]);
      }
      sessionStorage.setItem("em_admin_key", k);
      setAuthed(true);
    } catch (e) {
      sessionStorage.removeItem("em_admin_key");
      setAuthed(false);
      setError(e.response?.status === 401 ? "Invalid access key." : "Could not load applications.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (ref, status) => {
    try {
      await axios.patch(
        `${API}/applications/${ref}/status`,
        { status },
        { headers: { "X-Admin-Key": key } }
      );
      setApplications((apps) =>
        apps.map((a) => (a.reference === ref ? { ...a, status } : a))
      );
      toast.success(`${ref} updated to "${status}"`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("em_admin_key");
    setAuthed(false);
    setKey("");
    setApplications([]);
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="relative bg-[#fffdf9] border border-[#e3d3ac] p-10 lg:p-14 max-w-md w-full shadow-[0_30px_80px_rgba(45,26,11,0.22)]">
          <span
            className="absolute bottom-0 left-0 right-0 h-[3px]"
            style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
          />
          <span
            className="mx-auto w-11 h-11 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
            style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
          >
            <Vault className="-rotate-45 w-5 h-5 text-white" />
          </span>
          <h1 className="font-serif font-semibold text-3xl text-cream text-center">
            Vault <span className="italic gold-text">Admin</span>
          </h1>
          <p className="mt-4 text-sm text-sand text-center">
            Enter your access key to view submitted applications.
          </p>
          <form
            className="mt-8 space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
          >
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Access key"
              data-testid="admin-key-input"
              className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-5 py-4 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-xs text-red-700">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading || !key}
              data-testid="admin-login-button"
              className="sheen-btn w-full inline-flex items-center justify-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.02] transition-transform disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
            >
              {loading ? "Checking…" : "Open The Vault"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="pt-20 min-h-screen px-6 lg:px-12 pb-24">
      <div className="max-w-6xl mx-auto pt-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold mb-3">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
              Vault Admin
            </p>
            <h1 className="font-serif font-semibold text-3xl sm:text-4xl text-[#f3e5c8]">
              Applications <span className="italic gold-text">({applications.length})</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-button"
              className="btn-outline-lux inline-flex items-center gap-2 text-champagne text-[11px] font-mono-accent uppercase tracking-[0.2em] px-5 py-3 hover:text-gold"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="btn-outline-lux inline-flex items-center gap-2 text-champagne text-[11px] font-mono-accent uppercase tracking-[0.2em] px-5 py-3 hover:text-gold"
            >
              <LogOut className="w-3.5 h-3.5" /> Log Out
            </button>
          </div>
        </div>

        {bookings.length > 0 && (
          <div className="mb-12">
            <p className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold mb-6">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
              Strategy Calls ({bookings.length})
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {bookings.map((b) => (
                <div
                  key={b.reference}
                  data-testid={`admin-booking-${b.reference}`}
                  className="relative bg-[#fffdf9] border border-[#e3d3ac] p-6 shadow-[0_15px_40px_rgba(45,26,11,0.1)]"
                >
                  <span
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                  />
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <span className="font-serif text-lg text-cream">{b.name}</span>
                    <span className="font-mono-accent text-xs text-gold">{b.reference}</span>
                  </div>
                  <p className="text-sm text-sand">
                    {new Date(`${b.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {b.time} CT
                  </p>
                  <p className="mt-1 text-sm text-sand break-all">{b.email}</p>
                  {b.application_reference && (
                    <p className="mt-1 text-xs font-mono-accent text-gold">Application: {b.application_reference}</p>
                  )}
                  {b.note && <p className="mt-3 pt-3 border-t border-[#e3d3ac] text-xs text-stone italic">&quot;{b.note}&quot;</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {applications.length === 0 ? (
          <p data-testid="admin-empty" className="text-sm text-sand">No applications yet.</p>
        ) : (
          <div className="space-y-5">
            {applications.map((a) => (
              <div
                key={a.reference}
                data-testid={`admin-application-${a.reference}`}
                className="relative bg-[#fffdf9] border border-[#e3d3ac] p-7 shadow-[0_15px_40px_rgba(45,26,11,0.1)]"
              >
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                />
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono-accent text-sm tracking-[0.15em] text-gold">{a.reference}</span>
                    <span className="font-serif text-xl text-cream">{a.artist_label_name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={a.status}
                      onChange={(e) => updateStatus(a.reference, e.target.value)}
                      data-testid={`admin-status-${a.reference}`}
                      className="bg-[#faf7ef] border border-[#c9a227]/40 text-xs text-cream px-3 py-2 focus:outline-none focus:border-[#c9a227]"
                    >
                      {statuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <span className="text-xs font-mono-accent text-stone">
                      {a.timestamp ? new Date(a.timestamp).toLocaleString() : ""}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 text-sm">
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Applicant</span><span className="text-sand">{a.name}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Email</span><span className="text-sand break-all">{a.email}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Phone</span><span className="text-sand">{a.phone || "—"}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Entity</span><span className="text-sand">{a.entity_type}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Funding Type</span><span className="text-sand">{a.funding_type}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Amount</span><span className="text-sand">{a.amount}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Revenue</span><span className="text-sand">{a.monthly_revenue || "—"}</span></div>
                  <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Catalog Age</span><span className="text-sand">{a.catalog_age || "—"}</span></div>
                </div>
                {a.message && (
                  <p className="mt-5 pt-4 border-t border-[#e3d3ac] text-sm text-sand italic">&quot;{a.message}&quot;</p>
                )}
                {a.documents && a.documents.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-[#e3d3ac]">
                    <p className="text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-3">
                      Documents ({a.documents.length})
                    </p>
                    <div className="space-y-2">
                      {a.documents.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-4 text-sm">
                          <span className="flex items-center gap-3 min-w-0">
                            <FileText className="w-4 h-4 text-gold shrink-0" />
                            <span className="text-sand truncate">{d.original_filename}</span>
                            <span className="text-[10px] font-mono-accent text-stone shrink-0">
                              {(d.size / 1024).toFixed(1)} KB
                            </span>
                          </span>
                          <button
                            onClick={() => downloadDoc(d)}
                            data-testid={`admin-download-${d.id}`}
                            className="inline-flex items-center gap-2 text-[10px] font-mono-accent uppercase tracking-[0.2em] text-champagne hover:text-gold transition-colors shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" /> Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
